import { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { MessageCircle, Check, X, Trash2, Search, ChevronLeft, ChevronRight } from 'lucide-react'


interface Comment {
    id: number
    article_id: number
    article_title?: string
    user_name?: string
    guest_name?: string
    guest_email?: string
    content: string
    status: 'pending' | 'approved' | 'rejected' | 'spam'
    created_at: string
}

export default function CommentsManagement() {
    const [comments, setComments] = useState<Comment[]>([])
    const [loading, setLoading] = useState(true)
    const [filter, setFilter] = useState<string>('all')
    const [search, setSearch] = useState('')
    const [page, setPage] = useState(1)
    const [totalPages, setTotalPages] = useState(1)

    useEffect(() => {
        fetchComments()
    }, [filter, page])

    const fetchComments = async () => {
        setLoading(true)
        try {
            const res = await fetch(`/api/admin/comments?status=${filter}&page=${page}&search=${search}`)
            const data = await res.json()
            if (data.success) {
                setComments(data.comments)
                setTotalPages(data.totalPages || 1)
            }
        } catch (error) {
            console.error('Failed to fetch comments:', error)
        } finally {
            setLoading(false)
        }
    }

    const updateStatus = async (id: number, status: string) => {
        try {
            const res = await fetch(`/api/admin/comments/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ status })
            })
            if ((await res.json()).success) {
                setComments(prev => prev.map(c => c.id === id ? { ...c, status: status as any } : c))
            }
        } catch (error) {
            console.error('Failed to update:', error)
        }
    }

    const deleteComment = async (id: number) => {
        if (!confirm('Delete this comment?')) return
        try {
            const res = await fetch(`/api/admin/comments/${id}`, { method: 'DELETE' })
            if ((await res.json()).success) {
                setComments(prev => prev.filter(c => c.id !== id))
            }
        } catch (error) {
            console.error('Failed to delete:', error)
        }
    }

    const statusColors: Record<string, string> = {
        pending: 'bg-yellow-100 text-yellow-800',
        approved: 'bg-green-100 text-green-800',
        rejected: 'bg-red-100 text-red-800',
        spam: 'bg-gray-100 text-gray-800'
    }

    const filters = [
        { key: 'all', label: 'All' },
        { key: 'pending', label: 'Pending' },
        { key: 'approved', label: 'Approved' },
        { key: 'rejected', label: 'Rejected' },
        { key: 'spam', label: 'Spam' }
    ]

    return (
        <>
            <Helmet><title>Comments Management | Admin</title></Helmet>

            <div className="p-6">
                <div className="mb-6">
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <MessageCircle className="w-6 h-6 text-primary-600" />
                        Comments Management
                    </h1>
                    <p className="text-gray-600">Manage article comments</p>
                </div>

                {/* Filters & Search */}
                <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
                    <div className="flex flex-wrap gap-4 items-center justify-between">
                        <div className="flex gap-2">
                            {filters.map(f => (
                                <button
                                    key={f.key}
                                    onClick={() => { setFilter(f.key); setPage(1) }}
                                    className={`px-3 py-1.5 rounded-full text-sm ${filter === f.key ? 'bg-primary-600 text-white' : 'bg-gray-100 hover:bg-gray-200'}`}
                                >
                                    {f.label}
                                </button>
                            ))}
                        </div>
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search comments..."
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && fetchComments()}
                                className="pl-10 pr-4 py-2 border rounded-lg text-sm w-64"
                            />
                        </div>
                    </div>
                </div>

                {/* Comments Table */}
                <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                    {loading ? (
                        <div className="p-8 text-center text-gray-500">Loading...</div>
                    ) : comments.length === 0 ? (
                        <div className="p-8 text-center text-gray-500">No comments found</div>
                    ) : (
                        <table className="w-full">
                            <thead className="bg-gray-50 border-b">
                                <tr>
                                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Author</th>
                                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Comment</th>
                                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Article</th>
                                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Status</th>
                                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Date</th>
                                    <th className="text-right px-4 py-3 text-sm font-medium text-gray-600">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y">
                                {comments.map(comment => (
                                    <tr key={comment.id} className="hover:bg-gray-50">
                                        <td className="px-4 py-3">
                                            <div>
                                                <p className="font-medium text-sm">{comment.user_name || comment.guest_name}</p>
                                                {comment.guest_email && (
                                                    <p className="text-xs text-gray-400">{comment.guest_email}</p>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <p className="text-sm text-gray-700 line-clamp-2 max-w-md">{comment.content}</p>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className="text-sm text-gray-600">{comment.article_title || `Article #${comment.article_id}`}</span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`px-2 py-1 rounded-full text-xs ${statusColors[comment.status]}`}>
                                                {comment.status}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-500">
                                            {new Date(comment.created_at).toLocaleDateString()}
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <div className="flex items-center justify-end gap-1">
                                                {comment.status !== 'approved' && (
                                                    <button onClick={() => updateStatus(comment.id, 'approved')} className="p-1.5 text-green-600 hover:bg-green-50 rounded" title="Approve">
                                                        <Check className="w-4 h-4" />
                                                    </button>
                                                )}
                                                {comment.status !== 'rejected' && (
                                                    <button onClick={() => updateStatus(comment.id, 'rejected')} className="p-1.5 text-red-600 hover:bg-red-50 rounded" title="Reject">
                                                        <X className="w-4 h-4" />
                                                    </button>
                                                )}
                                                <button onClick={() => deleteComment(comment.id)} className="p-1.5 text-gray-400 hover:bg-gray-50 hover:text-red-600 rounded" title="Delete">
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="flex items-center justify-between px-4 py-3 border-t">
                            <button
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={page === 1}
                                className="flex items-center gap-1 px-3 py-1.5 border rounded disabled:opacity-50"
                            >
                                <ChevronLeft className="w-4 h-4" /> Prev
                            </button>
                            <span className="text-sm text-gray-500">Page {page} of {totalPages}</span>
                            <button
                                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                disabled={page === totalPages}
                                className="flex items-center gap-1 px-3 py-1.5 border rounded disabled:opacity-50"
                            >
                                Next <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </>
    )
}
